/**
 * Enterprise Data Export System
 * Comprehensive export capabilities for all schema entities
 * Production-ready with advanced features
 */

import type * as Schema from '@/../../shared/schema';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import JSZip from 'jszip';
// @ts-ignore
import { saveAs } from 'file-saver';

export type ExportFormat = 'csv' | 'excel' | 'json' | 'xml' | 'pdf' | 'zip' | 'sql';
export type ExportEntity = keyof typeof Schema;
export type ExportScope = 'all' | 'filtered' | 'selected';

export interface ExportField {
  name: string;
  label: string;
  type: string;
  format?: string;
  visible: boolean;
  order: number;
  transform?: (value: any) => any;
}

export interface ExportFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'between' | 'in' | 'not_in';
  value: any;
}

export interface ExportSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ExportConfiguration {
  entity: ExportEntity;
  format: ExportFormat;
  scope: ExportScope;
  fields: ExportField[];
  filters?: ExportFilter[];
  sort?: ExportSort[];
  includeHeaders: boolean;
  includeMetadata: boolean;
  compression: boolean;
  password?: string;
  batchSize: number;
  locale: string;
  timezone: string;
  dateFormat: string;
  numberFormat: string;
  currencyFormat: string;
  customTemplate?: string;
}

export interface ExportProgress {
  total: number;
  processed: number;
  currentBatch: number;
  totalBatches: number;
  status: 'pending' | 'processing' | 'exporting' | 'compressing' | 'completed' | 'failed';
  message?: string;
}

export interface ExportResult {
  success: boolean;
  fileUrl?: string;
  fileName: string;
  fileSize: number;
  format: ExportFormat;
  recordCount: number;
  duration: number;
  checksum?: string;
  metadata?: Record<string, any>;
  errors?: string[];
  warnings?: string[];
}

export interface ExportTemplate {
  id: string;
  name: string;
  entity: ExportEntity;
  configuration: ExportConfiguration;
  createdAt: Date;
  updatedAt: Date;
  isDefault: boolean;
}

export class ExportEngineError extends Error {
  constructor(
    message: string,
    public code: string,
    public entity?: ExportEntity,
    public format?: ExportFormat
  ) {
    super(message);
    this.name = 'ExportEngineError';
  }
}

export class ExportEngine {
  private config: ExportConfiguration;
  private progressCallbacks: Array<(progress: ExportProgress) => void> = [];
  private templates: Map<string, ExportTemplate> = new Map();

  constructor(config: Partial<ExportConfiguration> = {}) {
    // Default configuration
    this.config = {
      entity: 'customers',
      format: 'excel',
      scope: 'all',
      fields: [],
      includeHeaders: true,
      includeMetadata: true,
      compression: false,
      batchSize: 1000,
      locale: 'en-US',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateFormat: 'YYYY-MM-DD',
      numberFormat: '#,##0.00',
      currencyFormat: '$#,##0.00',
      ...config
    };

    this.loadDefaultTemplates();
  }

  private loadDefaultTemplates(): void {
    // Customers template
    this.templates.set('customers-default', {
      id: 'customers-default',
      name: 'Customer Master List',
      entity: 'customers',
      configuration: {
        entity: 'customers',
        format: 'excel',
        scope: 'all',
        fields: this.getDefaultFields('customers'),
        includeHeaders: true,
        includeMetadata: true,
        compression: false,
        batchSize: 1000,
        locale: 'en-US',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'YYYY-MM-DD',
        numberFormat: '#,##0.00',
        currencyFormat: '$#,##0.00'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isDefault: true
    });

    // Products template
    this.templates.set('products-default', {
      id: 'products-default',
      name: 'Product Catalog',
      entity: 'products',
      configuration: {
        entity: 'products',
        format: 'excel',
        scope: 'all',
        fields: this.getDefaultFields('products'),
        includeHeaders: true,
        includeMetadata: true,
        compression: false,
        batchSize: 1000,
        locale: 'en-US',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'YYYY-MM-DD',
        numberFormat: '#,##0.00',
        currencyFormat: '$#,##0.00'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isDefault: true
    });

    // Orders template
    this.templates.set('orders-default', {
      id: 'orders-default',
      name: 'Sales Orders',
      entity: 'orders',
      configuration: {
        entity: 'orders',
        format: 'excel',
        scope: 'all',
        fields: this.getDefaultFields('orders'),
        includeHeaders: true,
        includeMetadata: true,
        compression: false,
        batchSize: 1000,
        locale: 'en-US',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'YYYY-MM-DD',
        numberFormat: '#,##0.00',
        currencyFormat: '$#,##0.00'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      isDefault: true
    });
  }

  private getDefaultFields(entity: ExportEntity): ExportField[] {
    switch (entity) {
      case 'customers':
        return [
          { name: 'code', label: 'Customer Code', type: 'string', visible: true, order: 1 },
          { name: 'first_name', label: 'First Name', type: 'string', visible: true, order: 2 },
          { name: 'last_name', label: 'Last Name', type: 'string', visible: true, order: 3 },
          { name: 'email', label: 'Email', type: 'string', visible: true, order: 4 },
          { name: 'phone', label: 'Phone', type: 'string', visible: true, order: 5 },
          { name: 'address', label: 'Address', type: 'string', visible: true, order: 6 },
          { name: 'city', label: 'City', type: 'string', visible: true, order: 7 },
          { name: 'postal_code', label: 'Postal Code', type: 'string', visible: true, order: 8 },
          { name: 'country', label: 'Country', type: 'string', visible: true, order: 9 },
          { name: 'loyalty_points_balance', label: 'Loyalty Points', type: 'number', visible: true, order: 10 },
          { name: 'total_lifetime_value', label: 'Lifetime Value', type: 'currency', visible: true, order: 11 },
          { name: 'last_purchase_date', label: 'Last Purchase', type: 'date', visible: true, order: 12 },
          { name: 'customer_since_date', label: 'Customer Since', type: 'date', visible: true, order: 13 },
          { name: 'is_active', label: 'Active', type: 'boolean', visible: true, order: 14 }
        ];

      case 'products':
        return [
          { name: 'code', label: 'Product Code', type: 'string', visible: true, order: 1 },
          { name: 'name', label: 'Product Name', type: 'string', visible: true, order: 2 },
          { name: 'description', label: 'Description', type: 'string', visible: true, order: 3 },
          { name: 'category_id', label: 'Category ID', type: 'string', visible: true, order: 4 },
          { name: 'price', label: 'Price', type: 'currency', visible: true, order: 5 },
          { name: 'cost_price', label: 'Cost Price', type: 'currency', visible: true, order: 6 },
          { name: 'barcode', label: 'Barcode', type: 'string', visible: true, order: 7 },
          { name: 'is_active', label: 'Active', type: 'boolean', visible: true, order: 8 },
          { name: 'track_inventory', label: 'Track Inventory', type: 'boolean', visible: true, order: 9 },
          { name: 'minimum_stock', label: 'Minimum Stock', type: 'number', visible: true, order: 10 },
          { name: 'reorder_level', label: 'Reorder Level', type: 'number', visible: true, order: 11 },
          { name: 'reorder_quantity', label: 'Reorder Quantity', type: 'number', visible: true, order: 12 },
          { name: 'created_at', label: 'Created Date', type: 'date', visible: true, order: 13 },
          { name: 'updated_at', label: 'Updated Date', type: 'date', visible: true, order: 14 }
        ];

      case 'orders':
        return [
          { name: 'order_number', label: 'Order Number', type: 'string', visible: true, order: 1 },
          { name: 'order_date', label: 'Order Date', type: 'datetime', visible: true, order: 2 },
          { name: 'customer_id', label: 'Customer ID', type: 'string', visible: true, order: 3 },
          { name: 'status', label: 'Status', type: 'string', visible: true, order: 4 },
          { name: 'order_type', label: 'Order Type', type: 'string', visible: true, order: 5 },
          { name: 'subtotal', label: 'Subtotal', type: 'currency', visible: true, order: 6 },
          { name: 'discount_amount', label: 'Discount', type: 'currency', visible: true, order: 7 },
          { name: 'tax_amount', label: 'Tax', type: 'currency', visible: true, order: 8 },
          { name: 'total_amount', label: 'Total', type: 'currency', visible: true, order: 9 },
          { name: 'paid_amount', label: 'Paid', type: 'currency', visible: true, order: 10 },
          { name: 'payment_status', label: 'Payment Status', type: 'string', visible: true, order: 11 },
          { name: 'cash_register_id', label: 'Cash Register', type: 'string', visible: true, order: 12 },
          { name: 'business_day_id', label: 'Business Day', type: 'string', visible: true, order: 13 },
          { name: 'created_at', label: 'Created Date', type: 'datetime', visible: true, order: 14 }
        ];

      default:
        return [];
    }
  }

  addProgressCallback(callback: (progress: ExportProgress) => void): void {
    this.progressCallbacks.push(callback);
  }

  removeProgressCallback(callback: (progress: ExportProgress) => void): void {
    this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
  }

  private updateProgress(progress: ExportProgress): void {
    this.progressCallbacks.forEach(callback => {
      try {
        callback(progress);
      } catch (error) {
        console.error('Progress callback error:', error);
      }
    });
  }

  async export(data: any[]): Promise<ExportResult> {
    const startTime = Date.now();
    const fileName = this.generateFileName();

    try {
      this.updateProgress({
        total: data.length,
        processed: 0,
        currentBatch: 1,
        totalBatches: Math.ceil(data.length / this.config.batchSize),
        status: 'processing',
        message: 'Preparing data for export...'
      });

      // Apply filters
      const filteredData = await this.applyFilters(data);

      // Apply sorting
      const sortedData = await this.applySorting(filteredData);

      // Select and transform fields
      const processedData = await this.processData(sortedData);

      this.updateProgress({
        total: data.length,
        processed: data.length,
        currentBatch: 1,
        totalBatches: 1,
        status: 'exporting',
        message: `Exporting ${processedData.length} records...`
      });

      let blob: Blob;
      let format = this.config.format;

      switch (format) {
        case 'csv':
          blob = await this.exportCSV(processedData);
          break;
        case 'excel':
          blob = await this.exportExcel(processedData);
          break;
        case 'json':
          blob = await this.exportJSON(processedData);
          break;
        case 'xml':
          blob = await this.exportXML(processedData);
          break;
        case 'pdf':
          blob = await this.exportPDF(processedData);
          break;
        case 'zip':
          blob = await this.exportZIP(processedData);
          break;
        case 'sql':
          blob = await this.exportSQL(processedData);
          break;
        default:
          throw new ExportEngineError(
            `Unsupported export format: ${format}`,
            'UNSUPPORTED_FORMAT',
            this.config.entity,
            format
          );
      }

      // Apply compression if enabled
      if (this.config.compression) {
        blob = await this.compressBlob(blob);
        format = 'zip';
      }

      // Apply encryption if password provided
      if (this.config.password) {
        blob = await this.encryptBlob(blob, this.config.password);
      }

      const fileSize = blob.size;
      const fileUrl = URL.createObjectURL(blob);
      const duration = Date.now() - startTime;

      this.updateProgress({
        total: data.length,
        processed: data.length,
        currentBatch: 1,
        totalBatches: 1,
        status: 'completed',
        message: `Export completed successfully. File size: ${this.formatFileSize(fileSize)}`
      });

      return {
        success: true,
        fileUrl,
        fileName,
        fileSize,
        format,
        recordCount: processedData.length,
        duration,
        checksum: await this.calculateChecksum(blob),
        metadata: this.generateMetadata(processedData),
        warnings: this.generateWarnings(processedData)
      };

    } catch (error) {
      this.updateProgress({
        total: data.length,
        processed: 0,
        currentBatch: 0,
        totalBatches: 0,
        status: 'failed',
        message: error instanceof Error ? error.message : 'Export failed'
      });

      throw new ExportEngineError(
        error instanceof Error ? error.message : 'Export failed',
        'EXPORT_FAILED',
        this.config.entity,
        this.config.format
      );
    }
  }

  private generateFileName(): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const entity = this.config.entity.replace(/_/g, '-');

    let extension = this.config.format;
    if (this.config.compression) {
      extension = 'zip';
    }

    return `${entity}-export-${timestamp}.${extension}`;
  }

  private async applyFilters(data: any[]): Promise<any[]> {
    if (!this.config.filters || this.config.filters.length === 0) {
      return data;
    }

    return data.filter(item => {
      return this.config.filters!.every(filter => {
        const value = this.getNestedValue(item, filter.field);

        switch (filter.operator) {
          case 'equals':
            return value === filter.value;
          case 'not_equals':
            return value !== filter.value;
          case 'contains':
            return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
          case 'greater_than':
            return Number(value) > Number(filter.value);
          case 'less_than':
            return Number(value) < Number(filter.value);
          case 'between':
            return value >= filter.value[0] && value <= filter.value[1];
          case 'in':
            return Array.isArray(filter.value) && filter.value.includes(value);
          case 'not_in':
            return Array.isArray(filter.value) && !filter.value.includes(value);
          default:
            return true;
        }
      });
    });
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private async applySorting(data: any[]): Promise<any[]> {
    if (!this.config.sort || this.config.sort.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      for (const sort of this.config.sort!) {
        const aValue = this.getNestedValue(a, sort.field);
        const bValue = this.getNestedValue(b, sort.field);

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        if (comparison !== 0) {
          return sort.direction === 'desc' ? -comparison : comparison;
        }
      }
      return 0;
    });
  }

  private async processData(data: any[]): Promise<any[]> {
    // Sort fields by order
    const sortedFields = [...this.config.fields].sort((a, b) => a.order - b.order);

    return data.map(item => {
      const processedItem: any = {};

      sortedFields.forEach(field => {
        if (field.visible) {
          const value = this.getNestedValue(item, field.name);
          processedItem[field.label] = this.transformValue(value, field);
        }
      });

      return processedItem;
    });
  }

  private transformValue(value: any, field: ExportField): any {
    // Apply field-specific transformation
    if (field.transform) {
      return field.transform(value);
    }

    // Apply type-based formatting
    switch (field.type) {
      case 'date':
        return this.formatDate(value);
      case 'datetime':
        return this.formatDateTime(value);
      case 'currency':
        return this.formatCurrency(value);
      case 'number':
        return this.formatNumber(value);
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'percentage':
        return this.formatPercentage(value);
      default:
        return value;
    }
  }

  private formatDate(value: any): string {
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);

    const format = this.config.dateFormat;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    switch (format) {
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY/MM/DD':
        return `${year}/${month}/${day}`;
      default:
        return date.toLocaleDateString(this.config.locale);
    }
  }

  private formatDateTime(value: any): string {
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);

    const datePart = this.formatDate(value);
    const timePart = date.toLocaleTimeString(this.config.locale, { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    return `${datePart} ${timePart}`;
  }

  private formatCurrency(value: any): string {
    const num = Number(value);
    if (isNaN(num)) return String(value);

    const format = this.config.currencyFormat;
    if (format.includes('$')) {
      return `$${num.toFixed(2)}`;
    } else if (format.includes('€')) {
      return `€${num.toFixed(2)}`;
    } else if (format.includes('£')) {
      return `£${num.toFixed(2)}`;
    } else {
      return `${num.toFixed(2)}`;
    }
  }

  private formatNumber(value: any): string {
    const num = Number(value);
    if (isNaN(num)) return String(value);

    const format = this.config.numberFormat;
    if (format.includes(',')) {
      return num.toLocaleString(this.config.locale);
    } else {
      return num.toString();
    }
  }

  private formatPercentage(value: any): string {
    const num = Number(value);
    if (isNaN(num)) return String(value);

    return `${(num * 100).toFixed(2)}%`;
  }

  private async exportCSV(data: any[]): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const csv = Papa.unparse(data, {
          header: this.config.includeHeaders,
          delimiter: ',',
          quotes: true,
          escapeFormulae: true
        });

        const bom = '\uFEFF';
        resolve(new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' }));
      } catch (error) {
        reject(error);
      }
    });
  }

  private async exportExcel(data: any[]): Promise<Blob> {
    try {
      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data, {
        header: this.config.fields.filter(f => f.visible).map(f => f.label),
        dateNF: 'yyyy-mm-dd',
        cellDates: true
      });

      // Apply column widths
      const maxWidths: number[] = [];
      XLSX.utils.sheet_to_json(worksheet, { header: 1 }).forEach(row => {
        (row as any[]).forEach((cell: any, colIndex: number) => {
          const length = cell ? String(cell).length : 0;
          if (!maxWidths[colIndex] || length > maxWidths[colIndex]) {
            maxWidths[colIndex] = length;
          }
        });
      });

      worksheet['!cols'] = maxWidths.map(width => ({ 
        width: Math.min(Math.max(width + 2, 10), 50) 
      }));

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, this.config.entity);

      // Add metadata sheet if requested
      if (this.config.includeMetadata) {
        const metadata = this.generateMetadata(data);
        const metadataSheet = XLSX.utils.json_to_sheet([metadata]);
        XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadata');
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true
      });

      return new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
    } catch (error) {
      throw new ExportEngineError(
        error instanceof Error ? error.message : 'Excel export failed',
        'EXCEL_EXPORT_FAILED',
        this.config.entity
      );
    }
  }

  private async exportJSON(data: any[]): Promise<Blob> {
    const json = JSON.stringify(data, null, 2);
    return new Blob([json], { type: 'application/json' });
  }

  private async exportXML(data: any[]): Promise<Blob> {
    const xml = this.generateXML(data);
    return new Blob([xml], { type: 'application/xml' });
  }

  private generateXML(data: any[]): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += `<${this.config.entity}>\n`;

    data.forEach(item => {
      xml += '  <record>\n';
      Object.entries(item).forEach(([key, value]) => {
        const escapedKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
        xml += `    <${escapedKey}>${this.escapeXML(String(value))}</${escapedKey}>\n`;
      });
      xml += '  </record>\n';
    });

    xml += `</${this.config.entity}>`;
    return xml;
  }

  private escapeXML(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private async exportPDF(data: any[]): Promise<Blob> {
    // This would integrate with a PDF library like jsPDF
    // For now, create a simplified HTML-based PDF
    const html = this.generatePDFHTML(data);
    const blob = new Blob([html], { type: 'text/html' });

    // Note: In production, you'd use jsPDF or similar library
    // This is a simplified implementation
    return blob;
  }

  private generatePDFHTML(data: any[]): string {
    const fields = this.config.fields.filter(f => f.visible);

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${this.config.entity} Export</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .header { text-align: center; margin-bottom: 30px; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${this.config.entity.replace(/_/g, ' ').toUpperCase()}</h1>
          <p>Exported on ${new Date().toLocaleString()}</p>
          <p>Total Records: ${data.length}</p>
        </div>
        <table>
          <thead>
            <tr>
              ${fields.map(field => `<th>${field.label}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
    `;

    data.forEach(item => {
      html += '<tr>';
      fields.forEach(field => {
        const value = item[field.label] || '';
        html += `<td>${value}</td>`;
      });
      html += '</tr>';
    });

    html += `
          </tbody>
        </table>
        <div class="footer">
          <p>Generated by Elysium POS Export Engine</p>
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => {
              window.close();
            }, 1000);
          };
        </script>
      </body>
      </html>
    `;

    return html;
  }

  private async exportZIP(data: any[]): Promise<Blob> {
    const zip = new JSZip();

    // Add data file
    const dataBlob = await this.exportJSON(data);
    zip.file(`${this.config.entity}.json`, dataBlob);

    // Add metadata
    const metadata = this.generateMetadata(data);
    zip.file('metadata.json', JSON.stringify(metadata, null, 2));

    // Add schema information
    const schema = this.getSchemaInfo();
    zip.file('schema.txt', schema);

    // Generate ZIP
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });

    return zipBlob;
  }

  private getSchemaInfo(): string {
    return `
Elysium POS Export Schema
=========================
Entity: ${this.config.entity}
Export Date: ${new Date().toISOString()}
Fields: ${this.config.fields.filter(f => f.visible).length} visible of ${this.config.fields.length} total
Format: ${this.config.format}
Configuration: ${JSON.stringify(this.config, null, 2)}
    `.trim();
  }

  private async exportSQL(data: any[]): Promise<Blob> {
    const tableName = this.config.entity;
    const fields = this.config.fields.filter(f => f.visible).map(f => f.name);

    let sql = `-- Elysium POS SQL Export\n`;
    sql += `-- Entity: ${tableName}\n`;
    sql += `-- Generated: ${new Date().toISOString()}\n\n`;

    sql += `CREATE TABLE IF NOT EXISTS \`${tableName}_export\` (\n`;
    sql += `  id INT AUTO_INCREMENT PRIMARY KEY,\n`;

    fields.forEach((field, index) => {
      const type = this.getSQLType(field);
      sql += `  \`${field}\` ${type}`;
      if (index < fields.length - 1) sql += ',';
      sql += '\n';
    });

    sql += `);\n\n`;
    sql += `INSERT INTO \`${tableName}_export\` (\`${fields.join('`, `')}\`) VALUES\n`;

    data.forEach((item, rowIndex) => {
      const values = fields.map(field => {
        const value = item[field];
        return this.escapeSQL(value);
      });

      sql += `  (${values.join(', ')})`;
      if (rowIndex < data.length - 1) sql += ',';
      sql += '\n';
    });

    sql += `;\n`;

    return new Blob([sql], { type: 'text/sql' });
  }

  private getSQLType(fieldName: string): string {
    const field = this.config.fields.find(f => f.name === fieldName);
    if (!field) return 'TEXT';

    switch (field.type) {
      case 'number':
      case 'currency':
      case 'percentage':
        return 'DECIMAL(15, 2)';
      case 'date':
        return 'DATE';
      case 'datetime':
        return 'DATETIME';
      case 'boolean':
        return 'BOOLEAN';
      default:
        return 'TEXT';
    }
  }

  private escapeSQL(value: any): string {
    if (value === null || value === undefined) {
      return 'NULL';
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }

    // Escape string
    const escaped = String(value)
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "''")
      .replace(/\0/g, '\\0')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t');

    return `'${escaped}'`;
  }

  private async compressBlob(blob: Blob): Promise<Blob> {
    // Simple compression using gzip
    const stream = blob.stream();
    const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
    return await new Response(compressedStream).blob();
  }

  private async encryptBlob(blob: Blob, password: string): Promise<Blob> {
    // Note: This is a simplified encryption for demonstration
    // In production, use proper crypto libraries like Web Crypto API
    const arrayBuffer = await blob.arrayBuffer();
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    // Simple XOR encryption (not secure for production)
    const encryptedBuffer = new Uint8Array(arrayBuffer);
    for (let i = 0; i < encryptedBuffer.length; i++) {
      encryptedBuffer[i] ^= passwordBuffer[i % passwordBuffer.length];
    }

    return new Blob([encryptedBuffer], { type: blob.type });
  }

  private async calculateChecksum(blob: Blob): Promise<string> {
    const arrayBuffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private generateMetadata(data: any[]): Record<string, any> {
    return {
      entity: this.config.entity,
      recordCount: data.length,
      exportDate: new Date().toISOString(),
      format: this.config.format,
      configuration: {
        fields: this.config.fields.filter(f => f.visible).length,
        filters: this.config.filters?.length || 0,
        locale: this.config.locale,
        timezone: this.config.timezone
      },
      statistics: {
        earliestDate: this.findEarliestDate(data),
        latestDate: this.findLatestDate(data),
        totalValue: this.calculateTotalValue(data),
        averageValue: this.calculateAverageValue(data)
      }
    };
  }

  private findEarliestDate(data: any[]): string | null {
    const dateFields = this.config.fields.filter(f => f.type === 'date' || f.type === 'datetime');
    if (dateFields.length === 0) return null;

    let earliest: Date | null = null;

    data.forEach(item => {
      dateFields.forEach(field => {
        const value = item[field.label];
        if (value) {
          const date = new Date(value);
          if (!isNaN(date.getTime()) && (!earliest || date < earliest)) {
            earliest = date;
          }
        }
      });
    });

    return earliest ? (earliest as any).toISOString() : null;
  }

  private findLatestDate(data: any[]): string | null {
    const dateFields = this.config.fields.filter(f => f.type === 'date' || f.type === 'datetime');
    if (dateFields.length === 0) return null;

    let latest: Date | null = null;

    data.forEach(item => {
      dateFields.forEach(field => {
        const value = item[field.label];
        if (value) {
          const date = new Date(value);
          if (!isNaN(date.getTime()) && (!latest || date > latest)) {
            latest = date;
          }
        }
      });
    });

    return latest ? (latest as any).toISOString() : null;
  }

  private calculateTotalValue(data: any[]): number {
    const currencyFields = this.config.fields.filter(f => f.type === 'currency');
    let total = 0;

    data.forEach(item => {
      currencyFields.forEach(field => {
        const value = Number(item[field.label]) || 0;
        total += value;
      });
    });

    return total;
  }

  private calculateAverageValue(data: any[]): number {
    const total = this.calculateTotalValue(data);
    const currencyFields = this.config.fields.filter(f => f.type === 'currency');
    const count = data.length * currencyFields.length;

    return count > 0 ? total / count : 0;
  }

  private generateWarnings(data: any[]): string[] {
    const warnings: string[] = [];

    // Check for empty fields
    this.config.fields.forEach(field => {
      if (field.visible) {
        const emptyCount = data.filter(item => !item[field.label]).length;
        if (emptyCount > 0) {
          warnings.push(`${emptyCount} records have empty values for '${field.label}'`);
        }
      }
    });

    // Check for data consistency
    if (data.length > 10000) {
      warnings.push('Large dataset: Consider using batch export for better performance');
    }

    return warnings;
  }

  private formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }

  // Template management
  saveTemplate(name: string, isDefault: boolean = false): string {
    const templateId = `${this.config.entity}-${Date.now()}`;
    const template: ExportTemplate = {
      id: templateId,
      name,
      entity: this.config.entity,
      configuration: { ...this.config },
      createdAt: new Date(),
      updatedAt: new Date(),
      isDefault
    };

    this.templates.set(templateId, template);
    return templateId;
  }

  loadTemplate(templateId: string): boolean {
    const template = this.templates.get(templateId);
    if (template) {
      this.config = { ...template.configuration };
      return true;
    }
    return false;
  }

  getTemplates(entity?: ExportEntity): ExportTemplate[] {
    let templates = Array.from(this.templates.values());

    if (entity) {
      templates = templates.filter(t => t.entity === entity);
    }

    return templates.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  deleteTemplate(templateId: string): boolean {
    return this.templates.delete(templateId);
  }

  // Utility methods
  async preview(data: any[], maxRows: number = 10): Promise<any[]> {
    const processedData = await this.processData(data.slice(0, maxRows));
    return processedData;
  }

  getSupportedFormats(): Array<{ format: ExportFormat; description: string; mimeType: string }> {
    return [
      { format: 'csv', description: 'Comma Separated Values', mimeType: 'text/csv' },
      { format: 'excel', description: 'Microsoft Excel', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      { format: 'json', description: 'JavaScript Object Notation', mimeType: 'application/json' },
      { format: 'xml', description: 'eXtensible Markup Language', mimeType: 'application/xml' },
      { format: 'pdf', description: 'Portable Document Format', mimeType: 'application/pdf' },
      { format: 'zip', description: 'Compressed Archive', mimeType: 'application/zip' },
      { format: 'sql', description: 'SQL Script', mimeType: 'text/sql' }
    ];
  }

  downloadFile(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  async exportToMultipleFormats(
    data: any[],
    formats: ExportFormat[]
  ): Promise<Map<ExportFormat, ExportResult>> {
    const results = new Map<ExportFormat, ExportResult>();

    for (const format of formats) {
      const originalFormat = this.config.format;
      this.config.format = format;

      try {
        const result = await this.export(data);
        results.set(format, result);
      } catch (error) {
        results.set(format, {
          success: false,
          fileName: '',
          fileSize: 0,
          format,
          recordCount: 0,
          duration: 0,
          errors: [error instanceof Error ? error.message : 'Export failed']
        });
      }

      this.config.format = originalFormat;
    }

    return results;
  }
}

// Export singleton instance
export const exportEngine = new ExportEngine();

// Helper function for quick exports
export async function quickExport(
  entity: ExportEntity,
  data: any[],
  format: ExportFormat = 'excel',
  options?: Partial<ExportConfiguration>
): Promise<ExportResult> {
  const engine = new ExportEngine({
    entity,
    format,
    ...options
  });

  return engine.export(data);
}